import { useEffect, useState } from 'react';

interface AdSenseProps {
  style?: React.CSSProperties;
  className?: string;
  adSlot?: string;
  variant?: 'compact' | 'responsive';
  shouldDisplay?: boolean; // Accept toggle state from parent
}

declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

export function AdSense({ 
  style, 
  className, 
  adSlot, 
  variant = 'responsive',
  shouldDisplay = false // Default to false if not provided
}: AdSenseProps) {
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    // Only load Google AdSense script if ads should be displayed and script not already loaded
    if (shouldDisplay && !scriptLoaded) {
      const loadAdSenseScript = () => {
        // Check if script already exists to avoid duplicates
        const existingScript = document.querySelector('script[src*="adsbygoogle.js"]');
        if (existingScript) {
          setScriptLoaded(true);
          return;
        }

        // Create and load the Google AdSense script
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1800961932308887';
        script.crossOrigin = 'anonymous';
        
        script.onload = () => {
          setScriptLoaded(true);
        };
        
        script.onerror = () => {
          console.error('Failed to load Google AdSense script');
          setScriptLoaded(false);
        };

        document.head.appendChild(script);
      };

      loadAdSenseScript();
    }
  }, [shouldDisplay, scriptLoaded]);

  useEffect(() => {
    // Only try to initialize AdSense if we should display ads and script is loaded
    if (shouldDisplay && scriptLoaded) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (err) {
        console.error('AdSense error:', err);
      }
    }
  }, [shouldDisplay, scriptLoaded]);

  // Don't render anything if ads are disabled
  if (!shouldDisplay) {
    return null;
  }

  const isCompact = variant === 'compact';

  return (
    <ins
      className={`adsbygoogle ${className || ''}`}
      style={{
        display: 'block',
        ...(isCompact ? {
          height: '100px',
          overflow: 'hidden',
        } : {}),
        ...style,
      }}
      data-ad-client="ca-pub-1800961932308887"
      data-ad-slot={adSlot}
      data-ad-format={isCompact ? "horizontal" : "auto"}
      data-full-width-responsive={isCompact ? "false" : "true"}
    />
  );
} 